/**
 * @fileoverview Projects Page
 * @description Landing view listing every evaluation project in the workspace.
 */

import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  CalendarRange,
  FileUp,
  FolderPlus,
  FolderKanban,
  Layers,
  MoreVertical,
  Pencil,
  Play,
  Plus,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { EmptyState, ScoreChip, SummaryMetric } from '../components/common';
import { NameDialog } from '../components/NameDialog';
import {
  addRun,
  createCycle,
  createProject,
  createScenario,
  deleteProject,
  ingestRun,
  projectStats,
  updateProject,
} from '../utils/evaluationStore';
import { toEvaluatorView, bandForScore } from '../utils/evaluationView';
import { parseEvaluationResults } from '../utils/evaluationParser';
import { scoutSample } from '../data/scoutSample';

/**
 * Render a single project card.
 * @param {Object} props - Project record and card actions.
 * @returns {JSX.Element} Project card.
 */
function ProjectCard({ project, onOpen, onEdit, onDelete }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const stats = projectStats(project);

  return (
    <Paper className="aier-project-card" elevation={0} sx={{ '--project-color': project.color }}>
      <Box
        className="aier-project-body"
        role="button"
        tabIndex={0}
        onClick={() => onOpen(project)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpen(project);
          }
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box className="aier-project-icon" sx={{ backgroundColor: `${project.color}14`, color: project.color }}>
            <FolderKanban size={20} />
          </Box>
          <Box flex={1} minWidth={0}>
            <Typography className="aier-project-name">{project.name}</Typography>
            <Typography className="aier-project-description">
              {project.description || 'No description'}
            </Typography>
          </Box>
          {stats.averageScore !== null && (
            <ScoreChip score={stats.averageScore} band={bandForScore(stats.averageScore)} />
          )}
        </Stack>

        <Stack className="aier-project-chips" direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip size="small" variant="outlined" icon={<CalendarRange size={12} />} label={`${stats.cycleCount} cycles`} />
          <Chip size="small" variant="outlined" icon={<Layers size={12} />} label={`${stats.scenarioCount} scenarios`} />
          <Chip size="small" variant="outlined" icon={<Play size={12} />} label={`${stats.runCount} runs`} />
        </Stack>

        <Typography className="aier-project-activity">
          {stats.lastActivity
            ? `Latest run ${new Date(stats.lastActivity).toLocaleDateString()}`
            : 'No runs yet'}
        </Typography>
      </Box>

      <IconButton
        size="small"
        className="aier-project-menu"
        onClick={(event) => setMenuAnchor(event.currentTarget)}
        aria-label={`Actions for ${project.name}`}
      >
        <MoreVertical size={16} />
      </IconButton>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => { setMenuAnchor(null); onEdit(project); }}>
          <Pencil size={14} style={{ marginRight: 8 }} /> Rename
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onDelete(project); }} sx={{ color: '#C81E3A' }}>
          <Trash2 size={14} style={{ marginRight: 8 }} /> Delete
        </MenuItem>
      </Menu>
    </Paper>
  );
}

/**
 * Render the evaluation projects landing page.
 * @param {Object} props - Workspace projects and change handler.
 * @returns {JSX.Element} Projects page.
 */
export function ProjectsPage({ projects, onProjectsChange }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [dialogState, setDialogState] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const totals = projects.reduce((accumulator, project) => {
    const stats = projectStats(project);
    return {
      cycles: accumulator.cycles + stats.cycleCount,
      scenarios: accumulator.scenarios + stats.scenarioCount,
      runs: accumulator.runs + stats.runCount,
    };
  }, { cycles: 0, scenarios: 0, runs: 0 });

  /**
   * Persist the dialog result as a new or renamed project.
   * @param {{name: string, description: string}} values - Submitted values.
   * @returns {void}
   */
  const handleDialogSubmit = (values) => {
    onProjectsChange(dialogState.project
      ? updateProject(dialogState.project.id, values)
      : createProject(values));
    setDialogState(null);
  };

  /**
   * File a result into its project, cycle, and scenario, creating any that are missing.
   * @param {React.ChangeEvent<HTMLInputElement>} event - File input change event.
   * @returns {Promise<void>} Resolves once the run is placed.
   */
  const handleRunFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    try {
      const view = toEvaluatorView(parseEvaluationResults(await file.text(), file.name));
      const placement = ingestRun(view);
      onProjectsChange(placement.projects);
      setErrorMessage('');
      navigate(`${placement.projectId}/${placement.cycleId}/${placement.scenarioId}/${placement.runId}`);
    } catch (parseError) {
      setErrorMessage(parseError.message);
    }
  };

  /**
   * Seed a demo project so the module can be explored without a file.
   * @returns {void}
   */
  const handleLoadSample = () => {
    const withProject = createProject({
      name: 'Scout AI Evaluation',
      description: 'Sample workspace showing a consolidated evaluator rollup.',
    });
    const project = withProject[withProject.length - 1];

    const withCycle = createCycle(project.id, {
      name: 'Cycle 1',
      description: 'Initial evaluation cycle.',
    });
    const cycle = withCycle.find((item) => item.id === project.id).cycles[0];

    const withScenario = createScenario(project.id, cycle.id, {
      name: 'Consolidated evaluators',
      description: 'Title, questions, category, operational context, and barrier causal evaluators.',
    });
    const scenario = withScenario
      .find((item) => item.id === project.id)
      .cycles.find((item) => item.id === cycle.id)
      .scenarios[0];

    const view = toEvaluatorView(parseEvaluationResults(scoutSample, 'scout-ai-evaluation.json'));
    onProjectsChange(addRun(project.id, cycle.id, scenario.id, view).projects);
  };

  return (
    <>
      <Box className="aier-header">
        <Stack direction="row" spacing={1.75} alignItems="flex-start">
          <Box className="aier-header-icon">
            <Sparkles size={24} />
          </Box>
          <Box>
            <Typography variant="h4" component="h1">AI Evals</Typography>
            <Typography color="text.secondary">
              Organize evaluation work into projects, track each scenario, and compare every run over time.
            </Typography>
          </Box>
        </Stack>
        {projects.length > 0 && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Plus size={16} />}
              onClick={() => setDialogState({ project: null })}
            >
              New project
            </Button>
            <Button
              variant="contained"
              startIcon={<FileUp size={16} />}
              onClick={() => fileInputRef.current?.click()}
            >
              Add run
            </Button>
          </Stack>
        )}
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        hidden
        data-testid="aier-run-input"
        onChange={handleRunFile}
      />

      {errorMessage && (
        <Alert severity="error" onClose={() => setErrorMessage('')}>{errorMessage}</Alert>
      )}

      {projects.length === 0 ? (
        <EmptyState
          icon={FolderPlus}
          title="Create your first evaluation project"
          message="A project groups related scenarios — for example one assistant, product area, or model. Each scenario keeps its own history of evaluation runs."
          hint="Adding a result file creates its project, test cycle, and scenario automatically when they do not exist yet."
        >
          <Button variant="contained" startIcon={<FileUp size={16} />} onClick={() => fileInputRef.current?.click()}>
            Add run
          </Button>
          <Button variant="outlined" startIcon={<Plus size={16} />} onClick={() => setDialogState({ project: null })}>
            New project
          </Button>
          <Button variant="outlined" startIcon={<Sparkles size={16} />} onClick={handleLoadSample}>
            Add sample project
          </Button>
        </EmptyState>
      ) : (
        <>
          <Box className="aier-summary-grid aier-summary-grid--three" aria-label="Workspace summary">
            <SummaryMetric
              label="Projects"
              value={projects.length}
              caption={`${totals.cycles} test cycles`}
              icon={FolderKanban}
              color="#01426A"
            />
            <SummaryMetric
              label="Scenarios"
              value={totals.scenarios}
              caption="Across all cycles"
              icon={Layers}
              color="#7C3AED"
            />
            <SummaryMetric
              label="Runs"
              value={totals.runs}
              caption="Across all scenarios"
              icon={Play}
              color="#0097A9"
            />
          </Box>

          <Box className="aier-card-grid">
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onOpen={(target) => navigate(target.id)}
                onEdit={(target) => setDialogState({ project: target })}
                onDelete={(target) => onProjectsChange(deleteProject(target.id))}
              />
            ))}
          </Box>
        </>
      )}

      <NameDialog
        open={Boolean(dialogState)}
        title={dialogState?.project ? 'Rename project' : 'New evaluation project'}
        nameLabel="Project name"
        initialValues={dialogState?.project}
        onCancel={() => setDialogState(null)}
        onSubmit={handleDialogSubmit}
      />
    </>
  );
}

export default ProjectsPage;
